import type { ReactNode } from "react";
import ModalHeader from "src/components/general/Modal/components/ModalHeader/ModalHeader";
import { ResizeHandle } from "src/components/general/ResizeHandle/ResizeHandle";
import AppPortal from "src/components/utils/AppPortal/AppPortal";
import { useDragResize1D } from "src/hooks/useDragResize1D/useDragResize1D";
import type { ChatSession } from "../../AiToolboxSection.types";
import { AiAssistantCreateSessionButton } from "../AiAssistantHeaderActions/components/AiAssistantCreateSessionButton/AiAssistantCreateSessionButton";
import ChatHistory from "../ChatHistory/ChatHistory";
import {
  SIDEBAR_DEFAULT_PX,
  SIDEBAR_MAX_PX,
  SIDEBAR_MIN_PX,
} from "./AiAssistantMaximizedShell.constants";

export interface AiAssistantMaximizedShellProps {
  onCollapse?: () => void;
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSwitchSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => void;
  onCreateNewSession: () => void;
  children: ReactNode;
}

const AiAssistantMaximizedShell = ({
  onCollapse,
  sessions,
  activeSessionId,
  onSwitchSession,
  onDeleteSession,
  onRenameSession,
  onCreateNewSession,
  children,
}: AiAssistantMaximizedShellProps) => {
  // ponytail: fixed max 480 — below ~700px viewport history can squish chat;
  // upgrade: container-measured max (ResizeObserver on the flex row).
  const { targetRef, handleRef } = useDragResize1D<HTMLElement>({
    axis: "horizontal",
    min: SIDEBAR_MIN_PX,
    max: SIDEBAR_MAX_PX,
    initialSize: SIDEBAR_DEFAULT_PX,
  });

  return (
    <AppPortal>
      <div
        className="fixed inset-0 z-modal bg-background-paper flex flex-col"
        data-ph-capture-attribute-feature="ai_assistant"
        data-test-id="ai-maximized-shell"
      >
        <ModalHeader title="AI Assistant" onClose={onCollapse} />

        <div className="flex flex-1 min-h-0">
          <aside
            ref={targetRef}
            className="border-r border-divider flex flex-col shrink-0 min-w-0 overflow-hidden bg-background-default"
          >
            <div className="flex items-center p-2">
              <AiAssistantCreateSessionButton
                onCreateNewSession={onCreateNewSession}
              >
                New chat
              </AiAssistantCreateSessionButton>
            </div>
            <h3 className="font-medium text-text-secondary p-4">History</h3>
            <div className="flex-1 overflow-y-auto">
              <ChatHistory
                sessions={sessions}
                activeSessionId={activeSessionId}
                onSwitchSession={onSwitchSession}
                onDeleteSession={onDeleteSession}
                onRenameSession={onRenameSession}
              />
            </div>
          </aside>

          <ResizeHandle ref={handleRef} className="shrink-0" />

          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            <div className="max-w-4xl mx-auto w-full flex-1 min-h-0 flex flex-col pb-3">
              {children}
            </div>
          </div>
        </div>
      </div>
    </AppPortal>
  );
};

export default AiAssistantMaximizedShell;
